/**
 * usageDrill — the frame the Usage report's drills share: the admin gate and
 * the breadcrumb back to the report.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCanViewUsage } from "../hooks/useWorkspaceRole.js";
import { Note } from "./usageParts.js";
import { UsageColumn } from "./UsageReportView.js";

/** Members never see another person's or an Agent's spend: the drill says
 * so and points at their own line. The server enforces the same gate; this
 * only spares a member the request. A god-mode operator reads it like an
 * owner. A mount with no session (tests) reads as allowed, and the server
 * decides. */
export function UsageDrillGate({ children }: { children: ReactNode }) {
  const isAdmin = useCanViewUsage(true);
  return (
    <UsageColumn>
      {isAdmin ? (
        children
      ) : (
        <div style={{ display: "contents" }}>
          <UsageBreadcrumb trail={[]} />
          <Note tone="honest">
            Team and Agent usage is visible to workspace admins. Your own line is on the Usage page.
          </Note>
        </div>
      )}
    </UsageColumn>
  );
}

export function UsageBreadcrumb({ trail }: { trail: string[] }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-3 flex flex-wrap items-center gap-1.5 text-xs"
      style={{ fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}
    >
      <Link to="/usage" className="text-[var(--accent)] no-underline hover:underline">
        Usage
      </Link>
      {trail.map((crumb, index) => (
        <span key={`${index}-${crumb}`} className="flex items-center gap-1.5">
          <span aria-hidden="true">/</span>
          <span aria-current={index === trail.length - 1 ? "page" : undefined}>{crumb}</span>
        </span>
      ))}
    </nav>
  );
}
